/* Footer updated: Added Instagram and Google social profile icons in the brand column.
   Contact column shows correspondence address with "(Kantooradres)" label, phone, email, and a WhatsApp "Kijkafspraak" link with an external-link icon and hover underline. Nursery address is intentionally omitted.
   Updated: WhatsApp link now uses the main number +31 85 02 33 564. */

import { ExternalLink, Instagram } from 'lucide-react';
import { trackGoal } from '@/lib/datafast';

const Footer = () => {
  return (
    <footer className="w-full bg-[#1e2b22] text-[#cfcac2]">
      <div className="mx-auto max-w-[1240px] px-5 lg:px-8 py-20">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          {/* Brand column */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <a href="https://www.buitn.nl" target="_blank" rel="noopener noreferrer" aria-label="Bezoek Buitn.nl">
                <img src="/assets/logos/buitnplanten-logo-beige.webp" alt="Buitn Planten" className="h-10 w-auto" />
              </a>
            </div>
            <p className="text-sm leading-relaxed text-[#cfcac2] max-w-[320px]">
              Haagplanten, bomen en tuinplanten voor een buitenruimte die klopt. Direct van de kweker, met persoonlijk advies.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a href="https://www.instagram.com/buitnplanten.nl/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[#8b867d] hover:text-[#e7e1d6] transition-colors">
                <Instagram size={20} strokeWidth={1.8} />
              </a>
              <a href="https://share.google/rRuGOX9cwYp6vbKci" target="_blank" rel="noopener noreferrer" aria-label="Google-bedrijfsprofiel" className="text-[#8b867d] hover:text-[#e7e1d6] transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </a>
            </div>
          </div>

          {/* Navigation columns */}
          <div className="md:col-span-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#9da59a] mb-5">Assortiment</h3>
            <ul className="space-y-3 text-sm">
              <li><a href="/haagplanten" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Haagplanten</a></li>
              <li><a href="/bomen" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Bomen</a></li>
              <li><a href="/tuinplanten" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Tuinplanten</a></li>
              <li><a href="/aanplant-en-verzorging" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Aanplant &amp; verzorging</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#9da59a] mb-5">Klantenservice</h3>
            <ul className="space-y-3 text-sm">
              <li><a href="/klantenservice/afhalen" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Afhalen</a></li>
              <li><a href="/klantenservice/betalen" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Betalen</a></li>
              <li><a href="/klantenservice/bezorging" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Bezorging</a></li>
              <li><a href="/klantenservice/garantie" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Garantie</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#9da59a] mb-5">Over ons</h3>
            <ul className="space-y-3 text-sm">
              <li><a href="/algemene-voorwaarden" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Algemene voorwaarden</a></li>
              <li><a href="/privacy" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Privacybeleid</a></li>
              <li><a href="/contact" className="text-[#cfcac2] hover:text-[#e7e1d6] transition-colors">Contact</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#9da59a] mb-5">Contact</h3>
            <ul className="space-y-3 text-sm text-[#cfcac2]">
              <li>Galileistraat 1N<br />7701 SK Dedemsvaart<br /><span className="text-[#a8a39a] text-xs">(Kantooradres)</span></li>
              <li><a href="tel:+31850233564" onClick={() => trackGoal('contact_engagement_clicked', { type: 'phone', source: 'footer' })} className="hover:text-[#e7e1d6] transition-colors">085 02 33 564</a></li>
              <li><a href="mailto:info@buitnplanten.nl" onClick={() => trackGoal('contact_engagement_clicked', { type: 'email', source: 'footer' })} className="hover:text-[#e7e1d6] transition-colors">info@buitnplanten.nl</a></li>
              <li><a href="https://wa.me/31850233564?text=Hallo%2C%20ik%20wil%20graag%20een%20kijkafspraak%20maken%20bij%20de%20kwekerij" target="_blank" rel="noopener noreferrer" onClick={() => trackGoal('contact_engagement_clicked', { type: 'whatsapp', source: 'footer' })} className="hover:text-[#e7e1d6] hover:underline hover:underline-offset-4 transition-colors">Benieuwd naar onze kwekerij? Maak een kijkafspraak&nbsp;<ExternalLink aria-hidden="true" size={13} strokeWidth={1.8} className="inline-block align-baseline" /></a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-[#3a4a3f]">
        <div className="mx-auto flex max-w-[1240px] flex-col items-center justify-between gap-2 px-5 py-6 text-xs text-[#9da59a] sm:flex-row lg:px-8">
          <p>© {new Date().getFullYear()} Buitn Planten · <a href="https://buitngroep.nl/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e7e1d6] transition-colors">onderdeel van Buitngroep.nl</a></p>
          <p>KVK 87887991 · BTW NL004509749B42</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
